import {
  Modal,
  Text,
  Title,
  Group,
  Badge,
  Stack,
  Table,
  Paper,
  Grid,
  Spoiler,
  Divider,
} from "@mantine/core";
import { JobPositionData } from "../../types/api";
import { IconBulb } from "../../shared/svg/IconBulb";

import { useTranslation } from "react-i18next";
import { UniversalTooltip } from "./UniversalTooltip";
import { getJobDetails } from "../../api/reportApi";
import { useEffect } from "react";
import { useMediaQuery } from "@mantine/hooks";
import useProfileStore from "../../store/useProfileStore";

interface JobDetailsModalProps {
  opened: boolean;
  onClose: () => void;
  job?: JobPositionData | null;
  setCurrentJob: (job: JobPositionData | null) => void;
}

export function JobDetailsModal({
  opened,
  onClose,
  job,
  setCurrentJob,
}: JobDetailsModalProps) {
  const { t } = useTranslation();
  const mobile = useMediaQuery("(max-width: 767px)");

  useEffect(() => {
    const body = document.querySelector(".mantine-Modal-content");
    if (body instanceof HTMLElement) {
      body.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [job?.exact_match.slug]);

  const { profileLanguage } = useProfileStore();

  const handleNewJob = async (slug: string) => {
    try {
      const res = await getJobDetails(slug, Object.keys(profileLanguage!)[0]);
      if (res) {
        setCurrentJob(res.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  if (!job) {
    return (
      <Modal opened={opened} onClose={onClose} title="Job Details" size="lg">
        <Text c="dimmed">{t("jobModal.noJob")}</Text>
      </Modal>
    );
  }
  const { exact_match, similar_matches } = job;

  const salaryMap: Record<string, { min?: number; max?: number }> = {};
  exact_match.salaries.forEach((s) => {
    if (!salaryMap[s.currency]) {
      salaryMap[s.currency] = {};
    }
    salaryMap[s.currency][s.type] = s.salary;
  });

  const salaryRows = Object.entries(salaryMap).map(([currency, values]) => (
    <Table.Tr key={currency}>
      <Table.Td>{currency}</Table.Td>
      <Table.Td>{values.min ?? "-"}</Table.Td>
      <Table.Td>{values.max ?? "-"}</Table.Td>
    </Table.Tr>
  ));

  return (
    <Modal
      zIndex={500}
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={4}>
          <Group gap="xs" align="center">
            <Text size="lg" fw={600}>
              {exact_match.title}
            </Text>
            <UniversalTooltip
              title={t("jobModal.funFactTitle")}
              label={exact_match.fun_fact}
              icon={<IconBulb />}
              zIndex={501}
            />
          </Group>

          <Stack gap={3}>
            <Text size="md" c="dimmed">
              {t("industry")}: {exact_match.industry}
            </Text>
            <Text size="md" c="dimmed">
              {t("education")}: {exact_match.education_level}
            </Text>
          </Stack>
        </Stack>
      }
      size="lg"
      padding="xl"
      styles={{ header: { alignItems: "start" } }}
    >
      <Stack>
        <Paper withBorder p="md" radius="md">
          <Title order={4}>{t("jobModal.description")}</Title>

          <Spoiler
            maxHeight={132}
            showLabel={
              <Text
                style={{
                  color: "#6c6c6c",
                  fontSize: "14px",
                  fontWeight: "bold",
                  right: 0,
                }}
              >
                {t("read_more")}
              </Text>
            }
            hideLabel={
              <Text
                style={{
                  color: "#6c6c6c",
                  fontSize: "14px",
                  fontWeight: "bold",
                  right: 0,
                }}
              >
                {t("read_less")}
              </Text>
            }
            transitionDuration={300}
          >
            <Text mt="xs">{exact_match.description}</Text>
          </Spoiler>
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Title order={4}>{t("jobModal.responsibilities")}</Title>
          <Text mt="xs">{exact_match.responsibilities}</Text>
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Title order={4}>{t("jobModal.requiredSkills")}</Title>
          <Grid mt="xs" gutter="md" align="stretch">
            <Grid.Col span={{ base: 12, sm: 5.5 }}>
              <Text fw={500} mb={8}>
                {t("jobModal.hardSkills")}
              </Text>
              <Group gap="xs" wrap="wrap">
                {exact_match.required_hard_skills.map((skill) => (
                  <Badge key={skill}>{skill}</Badge>
                ))}
              </Group>
            </Grid.Col>

            <Grid.Col
              span={{ base: 12, sm: 1 }}
              style={{
                display: !mobile ? "flex" : undefined,
                justifyContent: "center",
              }}
            >
              <Divider
                orientation="horizontal"
                visibleFrom="base"
                hiddenFrom="sm"
                my="sm"
              />
              <Divider
                orientation="vertical"
                visibleFrom="sm"
                size="sm"
                mx="auto"
                style={{ height: "100%" }}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 5.5 }}>
              <Text fw={500} mb={8}>
                {t("jobModal.softSkills")}
              </Text>
              <Group gap="xs" wrap="wrap">
                {exact_match.required_soft_skills.map((skill) => (
                  <Badge key={skill} color="teal">
                    {skill}
                  </Badge>
                ))}
              </Group>
            </Grid.Col>
          </Grid>
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Title order={4}>{t("jobModal.salaryRange")}</Title>
          <Table withColumnBorders mt="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("jobModal.currency")}</Table.Th>
                <Table.Th>{t("jobModal.min")}</Table.Th>
                <Table.Th>{t("jobModal.max")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{salaryRows}</Table.Tbody>
          </Table>
        </Paper>

        {similar_matches && similar_matches.length > 0 && (
          <Paper withBorder p="md" radius="md">
            <Title order={4}>{t("jobModal.similarRoles")}</Title>
            <Group gap="xs" mt="xs">
              {similar_matches.map((slug) => (
                <Badge
                  key={slug}
                  onClick={() => {
                    handleNewJob(slug);
                  }}
                  variant="outline"
                  className="clickable-badge"
                >
                  {slug.replace(/-/g, " ")}
                </Badge>
              ))}
            </Group>
          </Paper>
        )}
      </Stack>
    </Modal>
  );
}
